import {Color} from '@benchling/nucleus-ui';
import {Alert, Box, createStyles, Loader, SimpleGrid, Stack, Text} from '@mantine/core';
import React from 'react';

import {UNRECOGNIZED_FRAGMENTS_ERROR_CODE} from 'chemistry/ChemistryConstants';
import AutoCreateBaseCompoundChip from 'chemistry/molecules/AutoCreateBaseCompoundChip';
import {MoleculeFieldLabel, MoleculeFieldValue} from 'chemistry/molecules/tabs/MoleculeStructureField';
import getTestProps from 'test/lib/cypress/getTestProps';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FieldRequiredIndicator} from 'ui-library/inputs/FieldLabel';

import type {ParseCounterpartsResponse} from './MoleculeDAO';

const COUNTERPART_FIELDS_COMPONENT = 'ChemicalCounterpartFields';
const DEFAULT_PLACEHOLDER = 'Start by sketching a molecule';
const ERROR_PLACEHOLDER = 'Unable to identify molecule compound from the sketch';

const useStyles = createStyles(() => ({
  fieldGroup: {
    fontSize: 13,
  },
  fieldValue: {
    border: `1px solid ${Color.GRAY_300}`,
    cursor: 'not-allowed',
  },
  loader: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
}));

function getErrorAlertTitle(errorCode: string | null): string | undefined {
  switch (errorCode) {
    case UNRECOGNIZED_FRAGMENTS_ERROR_CODE:
      return 'Your structure contains one or more unrecognized fragments.';
    default:
      return undefined;
  }
}

function getErrorAlertMessage(errorCode: string | null, fallbackMessage: string): string {
  switch (errorCode) {
    case UNRECOGNIZED_FRAGMENTS_ERROR_CODE:
      return 'If you intended to include a salt or solvate with your molecule, contact your admin to add it to your organization\u2019s library before registering this molecule. Otherwise, simplify your structure to a single base molecule.';
    default:
      return fallbackMessage;
  }
}

function renderCompoundContent(
  result: ParseCounterpartsResponse | null,
  entityName: string | undefined,
  errorCode: string | null
): JSX.Element {
  if (result?.existingParents?.[0]) {
    return <BioentityChip id={result.existingParents[0].id} size={ChipSize.SMALL} />;
  }
  if (result?.baseStructure?.canonicalCxSmiles && result?.counterparts?.length) {
    return <AutoCreateBaseCompoundChip entityName={entityName} baseMolfile={result.baseStructure.molfile} />;
  }
  if (result?.baseStructure?.canonicalCxSmiles) {
    return <Text span={true}>{result.baseStructure.canonicalCxSmiles}</Text>;
  }
  return (
    <Text span={true}>
      {errorCode === UNRECOGNIZED_FRAGMENTS_ERROR_CODE ? ERROR_PLACEHOLDER : DEFAULT_PLACEHOLDER}
    </Text>
  );
}

interface ChemicalCounterpartFieldsProps {
  parseResponse: ParseCounterpartsResponse | null;
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;
  entityName?: string;
}

export default function ChemicalCounterpartFields({
  parseResponse,
  isLoading,
  error,
  errorCode,
  entityName,
}: ChemicalCounterpartFieldsProps): JSX.Element {
  const {classes} = useStyles();

  return (
    <Stack spacing="md">
      {error && (
        <Alert
          variant="error"
          icon={<BenchlingIcon icon="exclamation-circle" />}
          title={getErrorAlertTitle(errorCode)}
        >
          {getErrorAlertMessage(errorCode, error)}
        </Alert>
      )}

      <Box className={classes.fieldGroup}>
        <MoleculeFieldLabel>
          Molecule Compound
          <FieldRequiredIndicator /> {isLoading && <Loader size="xs" className={classes.loader} />}
        </MoleculeFieldLabel>
        <MoleculeFieldValue
          className={classes.fieldValue}
          {...getTestProps({component: COUNTERPART_FIELDS_COMPONENT, element: 'compoundValue'})}
        >
          {renderCompoundContent(parseResponse, entityName, errorCode)}
        </MoleculeFieldValue>
      </Box>

      <Box className={classes.fieldGroup}>
        <SimpleGrid cols={2} spacing={12}>
          <MoleculeFieldLabel>Salt or Solvate</MoleculeFieldLabel>
          <MoleculeFieldLabel>Stoichiometric Ratio</MoleculeFieldLabel>
        </SimpleGrid>
        {parseResponse?.counterparts?.length ? (
          parseResponse.counterparts.map((counterpart, index) => (
            <SimpleGrid key={counterpart.id} cols={2} spacing={12} mt={index > 0 ? 8 : 0}>
              <MoleculeFieldValue
                className={classes.fieldValue}
                {...getTestProps({
                  component: COUNTERPART_FIELDS_COMPONENT,
                  element: 'counterpartName',
                  index: String(index),
                })}
              >
                <Text span={true}>{counterpart.name}</Text>
              </MoleculeFieldValue>
              <MoleculeFieldValue
                className={classes.fieldValue}
                {...getTestProps({
                  component: COUNTERPART_FIELDS_COMPONENT,
                  element: 'counterpartRatio',
                  index: String(index),
                })}
              >
                <Text span={true}>
                  {counterpart.ratioNumerator}:{counterpart.ratioDenominator}
                </Text>
              </MoleculeFieldValue>
            </SimpleGrid>
          ))
        ) : (
          <SimpleGrid cols={2} spacing={12}>
            <MoleculeFieldValue className={classes.fieldValue}>
              <Text span={true}>Salts or solvates that you add to the sketch will populate here</Text>
            </MoleculeFieldValue>
            <MoleculeFieldValue className={classes.fieldValue}>
              <Text span={true}>Fragment : compound</Text>
            </MoleculeFieldValue>
          </SimpleGrid>
        )}
      </Box>
    </Stack>
  );
}
